import {ruleStructure, getRuleAccessBitIndex} from 'src/app/shared-utilities/models-old/utils-old/rule-structure';
import {IRuleInfo, IUserAccess} from '../models';

//#region FUNCTION getStoreAccessFlags
/**
 * Gets the store access flags for a given user based on their access.
 *
 * This function processes the user's store access information and generates an object
 * (`storeFlags`) that maps store IDs to boolean values, indicating whether a user has access
 * to specific stores and their associated rules. The flags are derived from the user's
 * access rights, which are represented as a bitwise integer for each store.
 *
 * @param {IUserAccess} userAccess - The user access object containing the user's store access data.
 * @returns {IUserAccess} - The updated `userAccess` object, now including the `storeFlags` for each store.
 *
 * @example
 * const userAccess = {
 *   storeList: ['store001', 'store002'],
 *   stores: {
 *     store001: 5,  // binary: 0101
 *     store002: 3   // binary: 0011
 *   }
 * };
 * const updatedUserAccess = getStoreAccessFlags(userAccess);
 * console.info(updatedUserAccess.storeFlags);
 */
export function getStoreAccessFlags(userAccess: IUserAccess): IUserAccess {
  if (userAccess && Object.keys(userAccess).length > 0) {
    const storeFlags: Record<string, boolean[]> = {};
    if (userAccess?.storeList?.length > 0) {
      userAccess.storeList.forEach((storeId: string): void => {
        storeFlags[storeId] = ruleStructure.index
          .map((rule: IRuleInfo): boolean => {
            const bitIndex = getRuleAccessBitIndex(rule);
            return Math.floor((userAccess.stores[storeId] / Math.pow(2, bitIndex)) % 2) === 1;
          });
      });
      userAccess.storeFlags = storeFlags;
    }
    return userAccess;
  }
}
//#endregion

/**
 * Store IDs that are present in both `storeList` and the `stores` permission map.
 * A store missing from either is not usable in the active session (FE-95).
 */
export function getUsableStoreIdsFromUserAccess(userAccess: IUserAccess | undefined | null): string[] {
  if (!userAccess) {
    return [];
  }
  const storeList: string[] = userAccess.storeList ?? [];
  const storesMap: Record<string, number> = userAccess.stores ?? {};
  return storeList.filter((storeId: string): boolean =>
    Object.prototype.hasOwnProperty.call(storesMap, storeId),
  );
}

/**
 * Session store IDs that are no longer usable because they were removed from
 * `storeList` and/or the `stores` permission map in `user_access`.
 */
export function getRevokedSessionStoreIds(
  sessionStoreIds: string[],
  userAccess: IUserAccess | undefined | null,
): string[] {
  const usableIds = new Set<string>(getUsableStoreIdsFromUserAccess(userAccess));
  return (sessionStoreIds ?? []).filter((storeId: string): boolean => !usableIds.has(storeId));
}

/**
 * True when both arrays contain the same IDs irrespective of order (FE-95 eviction dedupe).
 */
export function areSameStringIdSets(left: string[], right: string[]): boolean {
  if (left.length !== right.length) {
    return false;
  }
  const sortedLeft: string[] = [...left].sort();
  const sortedRight: string[] = [...right].sort();
  return sortedLeft.every((id: string, index: number): boolean => id === sortedRight[index]);
}
